import { useState, useMemo, useCallback } from 'react';
import { ArrowLeft, Trophy, Heart, Users, Sparkles, Crown, Medal, Award, Loader2, Building2, Globe, GraduationCap, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableCombobox } from '@/components/ui/searchable-combobox';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { useSelectedCohort } from '@/hooks/useSelectedCohort';
import { useShowcaseProjects, solutionTypeToKey } from '@/hooks/useProjects';
import { useProjectLikeCounts, useMyLikes, useToggleLike } from '@/hooks/useProjectLikes';
import { toast } from 'sonner';

const solutionTypeConfig: Record<string, { label: string; color: string }> = {
  assistant: { label: 'Asistente IA', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  automation: { label: 'Automatización', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
  data: { label: 'Análisis de Datos', color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' },
  content: { label: 'Contenido IA', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  vibecoding: { label: 'Vibecoding', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
};

type ViewType = 'cohort' | 'organization' | 'public';

export default function ShowcasePage() {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const isAuthenticated = !!user;

  // Safe to call always — returns empty enrollments when user is null
  const { enrollments } = useSelectedCohort();

  const defaultView: ViewType = isAuthenticated && enrollments.length > 0 ? 'cohort' : 'public';
  const [view, setView] = useState<ViewType>(defaultView);
  const [selectedCohortId, setSelectedCohortId] = useState<string>(enrollments[0]?.cohort_id || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [expandedProblems, setExpandedProblems] = useState<Set<string>>(new Set());
  const toggleProblem = useCallback((id: string) => setExpandedProblems(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  }), []);

  const { data: projects = [], isLoading } = useShowcaseProjects(
    view,
    view === 'cohort' ? selectedCohortId : undefined,
    view === 'organization' ? profile?.organization_id : undefined,
  );

  const projectIds = useMemo(() => projects.map(p => p.id), [projects]);
  const { data: likeCounts = {} } = useProjectLikeCounts(projectIds);
  const { data: myLikes = new Set<string>() } = useMyLikes(projectIds);
  const toggleLike = useToggleLike();

  const filteredProjects = useMemo(() => {
    let result = [...projects];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p =>
        p.name?.toLowerCase().includes(q) ||
        p.problem?.toLowerCase().includes(q) ||
        p.profiles?.full_name?.toLowerCase().includes(q)
      );
    }
    if (filterType !== 'all') {
      result = result.filter(p => solutionTypeToKey(p.solution_type) === filterType);
    }
    return result.sort((a, b) => (likeCounts[b.id] || 0) - (likeCounts[a.id] || 0));
  }, [projects, searchQuery, filterType, likeCounts]);

  const getRankIcon = (index: number) => {
    switch (index) {
      case 0: return <Crown className="w-6 h-6 text-yellow-400" />;
      case 1: return <Medal className="w-6 h-6 text-gray-300" />;
      case 2: return <Award className="w-6 h-6 text-amber-600" />;
      default: return <span className="w-6 h-6 flex items-center justify-center text-muted-foreground font-bold">{index + 1}</span>;
    }
  };

  const viewConfig: Record<ViewType, { label: string; icon: React.ElementType }> = {
    cohort: { label: 'Mi Cohorte', icon: GraduationCap },
    organization: { label: 'Mi Organización', icon: Building2 },
    public: { label: 'Públicos', icon: Globe },
  };

  const handleLike = (projectId: string) => {
    if (!isAuthenticated) {
      toast.info('Inicia sesión para dar "Me gusta" a un proyecto.');
      return;
    }
    toggleLike.mutate({ projectId, isLiked: myLikes.has(projectId) });
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate(isAuthenticated ? '/challenge' : '/')} className="shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border border-yellow-500/30">
                <Trophy className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Showcase de Proyectos</h1>
                <p className="text-muted-foreground text-sm">Explora los proyectos de la comunidad</p>
              </div>
            </div>
          </div>
        </div>

        {/* View Selector — only show cohort/org tabs for authenticated users */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex gap-2">
            {(Object.entries(viewConfig) as [ViewType, typeof viewConfig[ViewType]][]).map(([key, cfg]) => {
              const Icon = cfg.icon;
              // Hide cohort and organization tabs for non-authenticated users
              if (!isAuthenticated && key !== 'public') return null;
              const disabled = key === 'organization' && !profile?.organization_id;
              return (
                <Button
                  key={key}
                  variant={view === key ? 'default' : 'outline'}
                  size="sm"
                  className="gap-2"
                  disabled={disabled}
                  onClick={() => setView(key)}
                >
                  <Icon className="w-4 h-4" />
                  {cfg.label}
                </Button>
              );
            })}
          </div>

          {view === 'cohort' && enrollments.length > 0 && (
            <SearchableCombobox
              value={selectedCohortId ?? ''}
              onSelect={setSelectedCohortId}
              placeholder="Selecciona cohorte"
              searchPlaceholder="Buscar cohorte o ruta..."
              triggerClassName="w-full sm:w-[300px]"
              options={enrollments.map((e) => ({
                value: e.cohort_id,
                label: `Ruta: ${e.program_name} — ${e.cohort_name}`,
                keywords: [e.program_name, e.cohort_name],
              }))}
            />
          )}
        </div>

        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre, problema o autor..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder="Tipo de solución" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              {Object.entries(solutionTypeConfig).map(([key, cfg]) => (
                <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Stats */}
        <Card className="mb-8 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border-primary/20">
          <CardContent className="py-6">
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-foreground">{filteredProjects.length}</p>
                <p className="text-sm text-muted-foreground">Proyectos</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        )}

        {/* Empty State */}
        {!isLoading && filteredProjects.length === 0 && (
          <div className="text-center py-12">
            <Trophy className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">Sin proyectos</h3>
            <p className="text-muted-foreground">
              {view === 'cohort' ? 'No hay proyectos en esta cohorte aún.' :
               view === 'organization' ? 'No hay proyectos visibles para tu organización.' :
               'No hay proyectos públicos disponibles.'}
            </p>
          </div>
        )}

        {/* Projects Grid */}
        {!isLoading && filteredProjects.length > 0 && (
          <>
            <h2 className="sr-only">Listado de proyectos</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredProjects.map((project, index) => {
              const sKey = solutionTypeToKey(project.solution_type);
              const config = solutionTypeConfig[sKey] || solutionTypeConfig.assistant;
              const authorName = project.profiles?.full_name || 'Anónimo';
              const isLeader = index === 0;

              return (
                <Card
                  key={project.id}
                  className={`relative overflow-hidden transition-all duration-300 hover:shadow-lg ${
                    isLeader ? 'ring-2 ring-yellow-500/50 bg-yellow-500/5' : ''
                  }`}
                >
                  {/* Rank Badge */}
                  <div className="absolute top-3 left-3 z-10">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      index < 3 ? 'bg-background/80 backdrop-blur-sm border border-border' : 'bg-muted'
                    }`}>
                      {getRankIcon(index)}
                    </div>
                  </div>

                  <CardHeader className="pt-14 pb-3">
                    <Badge variant="outline" className={`w-fit ${config.color}`}>
                      {config.label}
                    </Badge>
                    <h3 className="font-semibold text-lg text-foreground mt-2 line-clamp-2">
                      {project.name}
                    </h3>
                    <p className={`text-sm text-muted-foreground ${!expandedProblems.has(project.id) ? 'line-clamp-3' : ''}`}>
                      {project.problem || 'Sin descripción'}
                    </p>
                    {(project.problem?.length ?? 0) > 120 && (
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleProblem(project.id); }}
                        className="text-xs text-primary mt-0.5 hover:underline">
                        {expandedProblems.has(project.id) ? 'Ver menos' : 'Ver más'}
                      </button>
                    )}
                  </CardHeader>

                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Users className="w-4 h-4" />
                        <span className="truncate max-w-[160px]">{authorName}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-1.5 px-2 h-8"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLike(project.id);
                        }}
                        disabled={toggleLike.isPending}
                      >
                        <Heart className={`w-4 h-4 transition-colors ${myLikes.has(project.id) ? 'fill-destructive text-destructive' : 'text-muted-foreground'}`} />
                        <span className={`text-xs font-medium ${myLikes.has(project.id) ? 'text-destructive' : 'text-muted-foreground'}`}>
                          {likeCounts[project.id] || 0}
                        </span>
                      </Button>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(project.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          </>
        )}
      </div>
    </div>
  );
}
